// ScanDesk.jsx — the Existing Attachment Field Scan review desk
// (design_handoff_attachment_field_scan, scan-v2-desk).
//
// Queue rail (filters, progress, accept-all-high, scan banner) · attachment
// viewer (zoom/pan canvas + thumbnails) · editable extracted fields ·
// sticky apply/skip footer. One equipment record at a time; nothing writes
// to FMX without approval.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { T } from '../../theme';
import { Button, IconButton, Badge, Meter } from '../fmxkit';
import { ConfTag, AcceptCheck, ScanFilterChip, ScanThumb, CONF_COLOR, formatRelative, fmxEquipmentUrl } from './scanShared';
import { ScanFieldsSlideout, ScanKickoffModal } from './ScanConfig';
import ScanProgressPanel from './ScanProgress';
import { validateScanValue } from '../../equipmentScanWrite';

const INPUT_CSS = `
.afs-input { border: 1px solid ${T.border}; transition: border-color 120ms ease-out, box-shadow 120ms ease-out; }
.afs-input:hover { border-color: ${T.borderStrong}; }
.afs-input:focus { border-color: ${T.brand}; box-shadow: ${T.focusRing}; }
.scan-prog-row { animation: scanRowIn 0.25s ease-out both; }
@keyframes scanRowIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: none; } }
`;

// One editable field row: always an open text input with the current FMX
// value underneath. Commits on Enter/blur; Escape reverts the draft (and
// the apply checkbox); clearing to blank is a valid commit — it withdraws
// the value. While typing, the apply checkbox live-tracks whether the box
// has content.
function ScanFieldRow({ f, st, on, issue, onToggle, onCommit, onClear, onDraftChange, onLocate, last }) {
  const value = st ? st.value : f.value;
  const edited = st && st.edited;
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value, f.label]);
  const commit = (e) => {
    const v = draft.trim();
    if (v && v !== (value || '')) onCommit(v);
    else if (!v && (value || '')) { setDraft(''); onClear(); }
    else if (!v) setDraft('');
    if (e && e.type === 'keydown') e.target.blur();
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: last ? 'none' : `1px solid ${T.borderSubtle}` }}>
      <AcceptCheck on={!!on} onClick={value ? onToggle : undefined} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: `500 12.5px/16px ${T.fontSans}`, color: T.inkMuted, marginBottom: 2 }}>{f.label}</div>
        <input className="afs-input" value={draft}
          onChange={e => { setDraft(e.target.value); onDraftChange(!!e.target.value.trim()); }}
          onKeyDown={e => { if (e.key === 'Enter') commit(e); if (e.key === 'Escape') { setDraft(value || ''); onDraftChange(!!(value || '').trim()); e.target.blur(); } }}
          onBlur={() => commit()}
          style={{
            width: '100%', maxWidth: 250, height: 28, padding: '0 9px', borderRadius: T.radiusBtn,
            font: `600 13.5px/19px ${T.fontSans}`, color: T.ink, background: T.white, outline: 'none', boxSizing: 'border-box',
          }} />
        <div style={{ font: `400 11.5px/15px ${T.fontSans}`, fontStyle: 'italic', color: T.inkSoft, marginTop: 3 }}>
          Current: {f.cur != null && f.cur !== '' ? String(f.cur) : 'blank'}
        </div>
        {issue && value && (
          <div style={{ font: `500 11.5px/15px ${T.fontSans}`, color: T.red500, marginTop: 3 }}>
            {issue}
          </div>
        )}
      </div>
      {issue && value
        ? <Badge color="red" icon="triangle-exclamation">Won't save</Badge>
        : edited
          ? <Badge color="blueLight" icon="pencil">Edited</Badge>
          : (f.conf && value && <ConfTag conf={f.conf} />)}
      {f.srcName && (
        <span onClick={() => onLocate(f)} title="View source attachment"
          style={{ font: `400 11px/15px ${T.fontSans}`, color: T.brand, maxWidth: 132, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
          <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 9, marginRight: 4 }} />{f.srcName}
        </span>
      )}
    </div>
  );
}

// Scrollable, zoomable attachment canvas. Content width grows with zoom so
// the attachment can be panned by scrolling once zoomed in. ctrl/⌘ + wheel
// zooms (native listener so preventDefault works).
function AttachmentCanvas({ att, zoom, loading }) {
  // Anchored zoom: keep the point under the cursor fixed across zoom
  // changes — scroll' = (scroll + p) × (newZoom/prevZoom) − p. Falls back
  // to the viewport center when the cursor isn't over the canvas (e.g.
  // when zooming via the toolbar buttons).
  const scRef = useRef(null);
  const mouseRef = useRef(null);
  const prevZoomRef = useRef(zoom);
  useLayoutEffect(() => {
    const el = scRef.current;
    const pz = prevZoomRef.current;
    prevZoomRef.current = zoom;
    if (!el || pz === zoom) return;
    const m = mouseRef.current;
    const px = m ? m.x : el.clientWidth / 2;
    const py = m ? m.y : el.clientHeight / 2;
    const r = zoom / pz;
    el.scrollLeft = (el.scrollLeft + px) * r - px;
    el.scrollTop = (el.scrollTop + py) * r - py;
  }, [zoom]);
  const scrollerProps = {
    ref: scRef,
    onMouseMove: (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
    onMouseLeave: () => { mouseRef.current = null; },
  };
  if (loading) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: 'rgba(255,255,255,0.55)', background: '#14181f' }}>
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 22 }} />
        <span style={{ font: `500 12px/16px ${T.fontSans}` }}>Loading attachments from FMX…</span>
      </div>
    );
  }
  if (!att) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: 'rgba(255,255,255,0.55)', background: '#14181f' }}>
        <i className="fa-solid fa-image" style={{ fontSize: 28 }} />
        <span style={{ font: `500 12px/16px ${T.fontSans}` }}>No previewable attachments on this record</span>
      </div>
    );
  }
  const dataUrl = `data:${att.contentType};base64,${att.base64}`;
  if (att.classification === 'document') {
    return (
      <div {...scrollerProps} style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#252a33' }}>
        <div style={{ width: `${zoom * 100}%`, minWidth: '100%', height: '100%', padding: 16, boxSizing: 'border-box' }}>
          <iframe title={att.filename || 'attachment'} src={dataUrl}
            style={{ width: '100%', height: '100%', minHeight: 360, border: 'none', borderRadius: 2, background: T.white }} />
        </div>
      </div>
    );
  }
  return (
    <div {...scrollerProps} style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#14181f' }}>
      <div style={{ width: `${zoom * 100}%`, minWidth: '100%', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
        <img src={dataUrl} alt={att.filename || ''} style={{ width: '100%', display: 'block' }} />
      </div>
    </div>
  );
}

export default function ScanDesk({
  project, projectName, queue, fieldDefs, stats, scan, appliedIds,
  unscannedItems, hydrating, fmxError, syncedAt,
  loadAttachments, onApply, onStartScan, onToggleField, onOpenHistory,
}) {
  const unscannedCount = unscannedItems.length;
  const [curId, setCurId] = useState(null);
  const [file, setFile] = useState(0);
  const [zoom, setZoom] = useState(1);
  const viewerRef = useRef(null);
  const [vals, setVals] = useState({});      // `${id}|${label}` → { value, edited }
  const [acc, setAcc] = useState({});        // `${id}|${label}` → bool
  const [slideout, setSlideout] = useState(false);
  const [kickoff, setKickoff] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [bldFilter, setBldFilter] = useState(null);
  const [atts, setAtts] = useState(null);    // attachments for the active record; null = loading

  // Seed the accept map as queue items arrive: High-confidence values are
  // pre-checked, everything else starts unchecked.
  useEffect(() => {
    setAcc(prev => {
      const next = { ...prev };
      let changed = false;
      for (const q of queue) {
        for (const f of q.fields) {
          const k = `${q.id}|${f.label}`;
          if (f.value && !(k in next)) { next[k] = f.conf === 'High'; changed = true; }
        }
      }
      return changed ? next : prev;
    });
  }, [queue]);

  const matches = (q) => (!typeFilter || q.type === typeFilter) && (!bldFilter || q.bld === bldFilter);
  const visible = queue.filter(matches);
  const filtersOn = !!(typeFilter || bldFilter);
  const countBy = (fn, sel) => {
    const m = {};
    queue.forEach(q => { if (!sel || sel(q)) m[fn(q)] = (m[fn(q)] || 0) + 1; });
    return Object.keys(m).sort().map(name => ({ name, count: m[name] }));
  };
  const typeOptions = countBy(q => q.type, q => !bldFilter || q.bld === bldFilter);
  const bldOptions = countBy(q => q.bld, q => !typeFilter || q.type === typeFilter);

  const item = queue.find(q => q.id === curId) || visible[0] || queue[0] || null;
  useEffect(() => {
    if (item && item.id !== curId) setCurId(item.id);
  }, [item, curId]);

  // Load attachment bytes for the active record (cached upstream).
  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    setAtts(null);
    loadAttachments(item).then(list => { if (!cancelled) setAtts(list || []); });
    return () => { cancelled = true; };
  }, [item?.id]);

  const previewable = (atts || []).filter(a => a.base64 && !a.skipped);
  const att = previewable[Math.min(file, Math.max(0, previewable.length - 1))] || null;

  const k = (label) => `${item.id}|${label}`;
  const enabledDefs = fieldDefs.filter(d => d.on);
  const rows = item ? item.fields.filter(f => enabledDefs.some(d => d.label === f.label)) : [];
  const hidden = item ? item.fields.filter(f => f.value && !enabledDefs.some(d => d.label === f.label)).length : 0;

  const effValue = (f) => { const st = vals[k(f.label)]; return st ? st.value : f.value; };
  const withVal = rows.filter(f => effValue(f));
  const nAccepted = withVal.filter(f => acc[k(f.label)]).length;
  const nEdited = rows.filter(f => vals[k(f.label)]?.edited).length;

  // Pre-apply validation: values FMX would reject (unknown dropdown labels,
  // non-boolean checkboxes, …) block Apply — one bad row fails the whole
  // PUT server-side, so flagging up front is the honest UX.
  const defByLabel = new Map(fieldDefs.map(d => [d.label, d]));
  const issueOf = (f) => validateScanValue(defByLabel.get(f.label), effValue(f));
  const nBlocked = withVal.filter(f => acc[k(f.label)] && issueOf(f)).length;

  const reviewed = queue.filter(q => appliedIds.has(q.id)).length;
  const allDone = queue.length > 0 && reviewed === queue.length;

  const highCount = queue.reduce((n, q) =>
    n + q.fields.filter(f => f.value && f.conf === 'High' && !acc[`${q.id}|${f.label}`]).length, 0);

  const goTo = (id) => { setCurId(id); setFile(0); setZoom(1); setApplyError(null); };
  const toggle = (f) => setAcc(a => ({ ...a, [k(f.label)]: !a[k(f.label)] }));
  const commit = (f, v) => {
    setVals(m => ({ ...m, [k(f.label)]: { value: v, edited: true } }));
    setAcc(a => ({ ...a, [k(f.label)]: true }));
  };
  // Clearing a field withdraws it: nothing to apply, so it unchecks itself.
  const clearField = (f) => {
    setVals(m => ({ ...m, [k(f.label)]: { value: '', edited: false } }));
    setAcc(a => ({ ...a, [k(f.label)]: false }));
  };
  // While typing, the apply checkbox live-tracks whether the box has content.
  const draftSync = (f, hasText) => setAcc(a => (!!a[k(f.label)] === hasText ? a : { ...a, [k(f.label)]: hasText }));
  const locate = (f) => {
    const i = previewable.findIndex(a => String(a.id) === String(f.srcId) || a.filename === f.srcName);
    if (i >= 0) setFile(i);
  };

  // Advance to the next unreviewed record within the active filter; fall
  // back to any earlier unreviewed one. `extraDone` lets apply treat the
  // just-applied record as reviewed before state catches up.
  const advance = (extraDone) => {
    const pool = visible.length ? visible : queue;
    const isDone = (q) => appliedIds.has(q.id) || q.id === extraDone;
    const idx = pool.findIndex(q => q.id === item.id);
    const after = pool.slice(idx + 1).find(q => !isDone(q));
    const before = pool.find(q => q.id !== item.id && !isDone(q));
    if (after) goTo(after.id); else if (before) goTo(before.id);
  };

  const applyNext = async () => {
    if (!item || applying) return;
    const acceptedRows = withVal.filter(f => acc[k(f.label)]).map(f => ({
      kind: f.kind, key: f.key, label: f.label,
      value: effValue(f),
      confidence: f.conf,
      edited: !!vals[k(f.label)]?.edited,
      old: f.cur != null && f.cur !== '' ? String(f.cur) : null,
      sourceAttachmentId: f.srcId || null,
      sourceFilename: f.srcName || null,
    }));
    if (!acceptedRows.length) return;
    setApplying(true);
    setApplyError(null);
    try {
      await onApply(item, acceptedRows);
      advance(item.id);
    } catch (e) {
      setApplyError(e?.message || 'Failed to apply values to FMX');
    } finally {
      setApplying(false);
    }
  };
  // Skip: leave the record as Needs review — nothing is applied, just move on.
  const skipNext = () => { if (item) advance(null); };

  const acceptAllHigh = () => {
    setAcc(a => {
      const next = { ...a };
      queue.forEach(q => q.fields.forEach(f => { if (f.value && f.conf === 'High') next[`${q.id}|${f.label}`] = true; }));
      return next;
    });
  };

  // ctrl/⌘ + scroll zooms the attachment viewer; plain scroll pans it.
  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom(z => Math.min(2.6, Math.max(0.7, +(z + (e.deltaY < 0 ? 0.15 : -0.15)).toFixed(2))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [allDone, item?.id]);

  // ⏎ apply · S skip · ←/→ switch attachments. Suppressed while typing and
  // while the modal/slideout is open.
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || slideout || kickoff || !item) return;
      if (e.key === 'Enter') { e.preventDefault(); applyNext(); }
      else if (e.key === 's' || e.key === 'S') skipNext();
      else if (e.key === 'ArrowLeft') setFile(f => Math.max(0, f - 1));
      else if (e.key === 'ArrowRight') setFile(f => Math.min(previewable.length - 1, f + 1));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  const onFieldCount = enabledDefs.length;
  const spend = stats.spendUsd > 0 ? ` · $${stats.spendUsd.toFixed(2)} spend` : '';
  const statsLine = `${stats.scanned} scanned · ${stats.appliedCount} applied${spend}`;
  const curIdx = visible.findIndex(q => q.id === item?.id);

  const photoPane = (
    <div style={{ flex: '1 1 47%', minWidth: 340, display: 'flex', flexDirection: 'column' }}>
      <div ref={viewerRef} style={{ flex: 1, minHeight: 360, borderRadius: T.radiusTable, position: 'relative', overflow: 'hidden', background: '#14181f' }}>
        <AttachmentCanvas att={att} zoom={zoom} loading={atts === null} />
        <div style={{ position: 'absolute', right: 10, top: 10, display: 'flex', gap: 4, zIndex: 5 }}>
          {[
            ['magnifying-glass-plus', () => setZoom(z => Math.min(2.6, z + 0.2))],
            ['magnifying-glass-minus', () => setZoom(z => Math.max(0.7, z - 0.2))],
            ['up-right-and-down-left-from-center', () => setZoom(1)],
          ].map(([ic, fn]) => (
            <span key={ic} onClick={fn} style={{ width: 27, height: 27, borderRadius: T.radiusBtn, background: 'rgba(255,255,255,0.14)', color: T.white, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, cursor: 'pointer' }}>
              <i className={`fa-solid fa-${ic}`} />
            </span>
          ))}
        </div>
        <span style={{ position: 'absolute', left: 10, bottom: 10, zIndex: 5, pointerEvents: 'none', font: `500 11px/15px ${T.fontSans}`, color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.35)', padding: '2px 8px', borderRadius: T.radiusPill }}>
          {Math.round(zoom * 100)}% · scroll to pan · ⌘ scroll to zoom
        </span>
      </div>
      {previewable.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {previewable.map((a, i) => (
            <ScanThumb key={a.id || i} att={a} active={i === Math.min(file, previewable.length - 1)} onClick={() => setFile(i)} />
          ))}
        </div>
      )}
    </div>
  );

  const fieldsPane = item && (
    <div style={{ flex: '1 1 53%', minWidth: 380, minHeight: 0, overflowY: 'auto' }}>
      <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, overflow: 'hidden', boxShadow: T.shadowSm }}>
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.borderSubtle}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Extracted fields</span>
          <span style={{ marginLeft: 'auto', font: `500 12px/16px ${T.fontSans}`, color: T.inkMuted }}>
            {nAccepted} of {withVal.length} accepted{nEdited > 0 ? ` · ${nEdited} edited` : ''}
          </span>
        </div>
        {rows.map((f, i) => (
          <ScanFieldRow key={f.label} f={f} st={vals[k(f.label)]} on={acc[k(f.label)]} issue={issueOf(f)}
            onToggle={() => toggle(f)} onCommit={(v) => commit(f, v)} onClear={() => clearField(f)}
            onDraftChange={(hasText) => draftSync(f, hasText)} onLocate={locate}
            last={i === rows.length - 1} />
        ))}
        {rows.length === 0 && (
          <div style={{ padding: '18px 16px', font: `400 12.5px/17px ${T.fontSans}`, color: T.inkSoft }}>
            No fields enabled — open Fields to scan to switch some on.
          </div>
        )}
      </div>
      <div style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, padding: '8px 2px', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span>Type in any field to correct or fill it — edits are accepted automatically.</span>
        {hidden > 0 && (
          <span onClick={() => setSlideout(true)} style={{ color: T.brand, cursor: 'pointer' }}>
            {hidden} field{hidden > 1 ? 's' : ''} hidden by scan settings
          </span>
        )}
      </div>
      {applyError && (
        <div style={{ font: `400 12px/16px ${T.fontSans}`, color: T.red500, background: T.tint.error, border: `1px solid ${T.badge.red}`, borderRadius: T.radiusBtn, padding: '7px 10px', marginTop: 4 }}>
          {applyError}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      <style>{INPUT_CSS}</style>

      {/* compact header */}
      <div style={{ padding: '14px 24px 12px', borderBottom: `1px solid ${T.borderSubtle}`, background: T.white, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ font: `400 12.5px/16px ${T.fontSans}`, color: T.inkMuted, marginBottom: 2 }}>
            Projects <i className="fa-solid fa-chevron-right" style={{ fontSize: 8 }} /> {projectName}
          </div>
          <h2 style={{ font: `700 19px/25px ${T.fontSans}`, color: T.ink, margin: 0, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>Existing Attachment Field Scan</h2>
        </div>
        <span style={{ flex: 1 }} />
        {hydrating && (
          <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap' }}>
            <i className="fa-solid fa-rotate fa-spin" style={{ fontSize: 10, marginRight: 5 }} />syncing from FMX
          </span>
        )}
        {fmxError && (
          <span title={fmxError} style={{ font: `500 12px/16px ${T.fontSans}`, color: T.red500, whiteSpace: 'nowrap' }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 10, marginRight: 5 }} />FMX load failed
          </span>
        )}
        <span style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, whiteSpace: 'nowrap' }}>{statsLine}</span>
        <Button variant="neutral" icon="clock-rotate-left" size="sm" onClick={onOpenHistory}>History</Button>
        <Button variant="neutral" icon="list-check" size="sm" onClick={() => setSlideout(true)}>Fields to scan · {onFieldCount}</Button>
        <Button variant="primary" icon="camera" size="sm" disabled={hydrating || !!scan?.running || unscannedCount === 0} onClick={() => setKickoff(true)}>
          {hydrating ? 'Scan unscanned' : `Scan ${unscannedCount} unscanned`}
        </Button>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: T.bg }}>
        {/* queue rail */}
        <div style={{ width: 270, flexShrink: 0, borderRight: `1px solid ${T.borderSubtle}`, background: T.white, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${T.borderSubtle}` }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7 }}>
              <span style={{ font: `700 13px/18px ${T.fontSans}`, color: T.ink }}>Review queue</span>
              <span style={{ marginLeft: 'auto', font: `500 12px/16px ${T.fontSans}`, color: T.inkMuted }}>{reviewed} of {queue.length}</span>
            </div>
            <Meter pct={queue.length ? reviewed / queue.length * 100 : 0} h={5} fill={T.brand} />
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <ScanFilterChip label="Type" value={typeFilter} options={typeOptions} onChange={setTypeFilter} />
              <ScanFilterChip label="Building" value={bldFilter} options={bldOptions} onChange={setBldFilter} />
            </div>
            {filtersOn && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, font: `400 11.5px/15px ${T.fontSans}`, color: T.inkMuted }}>
                <span>Showing {visible.length} of {queue.length} in queue</span>
                <span onClick={() => { setTypeFilter(null); setBldFilter(null); }} style={{ marginLeft: 'auto', font: `600 11.5px/15px ${T.fontSans}`, color: T.brand, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <i className="fa-solid fa-filter-circle-xmark" style={{ fontSize: 10, marginRight: 4 }} />Clear
                </span>
              </div>
            )}
            {highCount > 0 && (
              <div onClick={acceptAllHigh} style={{ marginTop: 10, font: `600 12.5px/17px ${T.fontSans}`, color: T.brand, cursor: 'pointer' }}>
                <i className="fa-solid fa-check-double" style={{ fontSize: 11, marginRight: 5 }} />Accept all High confidence ({highCount})
              </div>
            )}
            {scan && (
              <div style={{ marginTop: 10 }}>
                {scan.finished ? (
                  <div style={{ padding: '8px 10px', borderRadius: T.radiusBtn, background: T.tint.success, border: `1px solid ${T.green500}`, font: `600 12px/16px ${T.fontSans}`, color: T.ink }}>
                    <i className="fa-solid fa-circle-check" style={{ color: T.green500, marginRight: 6 }} />Scan complete — results queued
                    {(() => {
                      const failed = Object.values(scan.status || {}).filter(s => s === 'error').length;
                      return failed > 0 ? <span style={{ font: `500 12px/16px ${T.fontSans}`, color: T.red500 }}> · {failed} failed</span> : null;
                    })()}
                  </div>
                ) : (
                  <ScanProgressPanel scan={scan} />
                )}
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {visible.map((q) => {
              const active = item && q.id === item.id;
              const isDone = appliedIds.has(q.id);
              const summary = { High: 0, Medium: 0, Low: 0 };
              q.fields.forEach(f => { if (f.value && summary[f.conf] != null) summary[f.conf]++; });
              return (
                <div className="scan-prog-row" key={q.id} onClick={() => goTo(q.id)} style={{
                  padding: '8px 10px', borderRadius: T.radiusTable, cursor: 'pointer',
                  background: active ? T.blue50 : 'transparent',
                  border: `1px solid ${active ? T.blue200 : 'transparent'}`,
                  opacity: isDone ? 0.55 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ font: `600 13px/18px ${T.fontSans}`, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.tag}</span>
                    <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{q.type}</span>
                    {isDone
                      ? <i className="fa-solid fa-circle-check" style={{ color: T.green500, fontSize: 13 }} />
                      : <span style={{ display: 'inline-flex', gap: 3 }}>
                          {['High', 'Medium', 'Low'].map(c => summary[c] > 0 && (
                            <span key={c} style={{ width: 7, height: 7, borderRadius: T.radiusPill, background: CONF_COLOR[c].dot }} />
                          ))}
                        </span>}
                  </div>
                  <div style={{ font: `400 11.5px/15px ${T.fontSans}`, color: T.inkSoft, marginTop: 1 }}>
                    {q.bld} · {q.fields.filter(f => f.value).length} fields
                  </div>
                </div>
              );
            })}
            {visible.length === 0 && queue.length > 0 && (
              <div style={{ textAlign: 'center', padding: '22px 12px', font: `400 12.5px/17px ${T.fontSans}`, color: T.inkSoft }}>
                <i className="fa-solid fa-filter-circle-xmark" style={{ fontSize: 18, display: 'block', marginBottom: 7 }} />
                No queued records match these filters.
              </div>
            )}
            {queue.length === 0 && (
              <div style={{ textAlign: 'center', padding: '22px 12px', font: `400 12.5px/17px ${T.fontSans}`, color: T.inkSoft }}>
                {hydrating
                  ? <span><i className="fa-solid fa-rotate fa-spin" style={{ fontSize: 12, display: 'block', marginBottom: 7 }} />Loading scan results…</span>
                  : 'Nothing scanned yet.'}
              </div>
            )}
          </div>
        </div>

        {/* review canvas */}
        {(allDone || !item) ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {queue.length === 0 && hydrating ? (
              <div style={{ textAlign: 'center', font: `400 13.5px/19px ${T.fontSans}`, color: T.inkMuted }}>
                <i className="fa-solid fa-rotate fa-spin" style={{ fontSize: 22, display: 'block', marginBottom: 10, color: T.inkSoft }} />
                Loading scan results and equipment from FMX…
              </div>
            ) : (
            <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, padding: '36px 44px', textAlign: 'center', maxWidth: 420, boxShadow: T.shadowSm }}>
              <i className={`fa-solid fa-${queue.length ? 'circle-check' : 'camera'}`} style={{ fontSize: 38, color: queue.length ? T.green500 : T.inkSoft }} />
              <div style={{ font: `700 19px/26px ${T.fontSans}`, color: T.ink, marginTop: 12 }}>
                {queue.length ? 'Review queue clear' : 'No scan results yet'}
              </div>
              <div style={{ font: `400 13.5px/19px ${T.fontSans}`, color: T.inkMuted, marginTop: 5 }}>
                {queue.length
                  ? `${reviewed} of ${queue.length} reviewed · accepted values are applied to Equipment custom fields in FMX.`
                  : 'Run a scan over the equipment attachments already stored in FMX — you choose which records, and results queue here for review.'}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 18 }}>
                <Button variant="primary" icon="camera" disabled={hydrating || !!scan?.running || unscannedCount === 0} onClick={() => setKickoff(true)}>
                  {hydrating ? 'Scan unscanned' : `Scan ${unscannedCount} unscanned`}
                </Button>
                <Button variant="neutral" onClick={onOpenHistory}>View applied history</Button>
              </div>
            </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${T.borderSubtle}`, background: T.white }}>
              <h3 style={{ font: `700 17px/23px ${T.fontSans}`, margin: 0, color: T.ink, whiteSpace: 'nowrap' }}>
                {item.tag}{item.type ? ` · ${item.type}` : ''}
              </h3>
              <a href={fmxEquipmentUrl(project?.fmx_site_url, item.id)} target="_blank" rel="noopener noreferrer"
                title="View this equipment record in FMX"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: `600 12.5px/18px ${T.fontSans}`, color: T.brand, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                View in FMX<i className="fa-solid fa-up-right-from-square" style={{ fontSize: 10 }} />
              </a>
              {appliedIds.has(item.id)
                ? <Badge color="green" icon="circle-check">Applied</Badge>
                : <Badge color="blueDark">Needs review</Badge>}
              <span style={{ font: `400 12.5px/17px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.bld} · scanned {formatRelative(item.scannedAt)}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ font: `500 12px/16px ${T.fontSans}`, color: T.inkMuted, whiteSpace: 'nowrap' }}>{(curIdx >= 0 ? curIdx : 0) + 1} of {visible.length}</span>
              <IconButton icon="chevron-left" title="Previous" onClick={() => { const i = Math.max(0, curIdx - 1); if (visible[i]) goTo(visible[i].id); }} />
              <IconButton icon="chevron-right" title="Next" onClick={() => { const i = Math.min(visible.length - 1, curIdx + 1); if (visible[i]) goTo(visible[i].id); }} />
            </div>
            {/* The row itself never scrolls — the attachment viewer stays
                pinned while the fields pane scrolls independently. */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', gap: 18, padding: 20, alignItems: 'stretch' }}>
              {fieldsPane}
              {photoPane}
            </div>
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.borderSubtle}`, background: T.white, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button variant="primary" icon="cloud-arrow-up" disabled={applying || nAccepted === 0 || nBlocked > 0} onClick={applyNext}>
                {applying ? 'Applying…'
                  : nBlocked > 0 ? `Fix ${nBlocked} flagged value${nBlocked === 1 ? '' : 's'}`
                  : `Apply ${nAccepted} accepted & next`}
              </Button>
              <Button variant="neutral" onClick={skipNext}>Skip</Button>
              <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft }}>
                Skipped records stay in the queue as needs review. Writes merge with existing custom fields — untouched values survive.
              </span>
              <span style={{ marginLeft: 'auto', font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap' }}>⏎ apply · S skip · ← → attachments</span>
            </div>
          </div>
        )}
      </div>

      {kickoff && (
        <ScanKickoffModal fields={fieldDefs} targets={unscannedItems}
          onEditFields={() => { setKickoff(false); setSlideout(true); }}
          onStart={(selectedIds) => { setKickoff(false); onStartScan(selectedIds); }}
          onClose={() => setKickoff(false)} />
      )}
      {slideout && (
        <ScanFieldsSlideout fields={fieldDefs} scannedCount={stats.scanned} siteUrl={project?.fmx_site_url} syncedAt={syncedAt}
          onToggle={onToggleField} onClose={() => setSlideout(false)} />
      )}
    </div>
  );
}
